import { InstagramOutlined, TikTokOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import laurine from 'assets/laurine.jpg';
import Logo from 'assets/logos/logo.svg?react';
import Ribbon from 'assets/ribbon.svg?react';
import { AxiosError } from 'axios';
import MedicalProfileCard, { getMedicalProfileState } from 'components/cards/MedicalProfileCard';
import ModuleCard from 'components/cards/ModuleCard';
import OctobreRoseCard from 'components/cards/OctobreRoseCard';
import PreventionJourneyModule from 'components/cards/PreventionJourneyModule';
import RebrandingAnnouncementCard from 'components/cards/RebrandingAnnouncementCard';
import LinearCalendarChart from 'components/charts/LinearCalendarChart';
import ChatsList from 'components/chats/ChatsList';
import { Page, PageContent } from 'components/layout/page';
import PageTopBar from 'components/navigation/PageTopBar';
import PartnerSection from 'components/PartnerSection';
import PostsList from 'components/PostsList';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { VisuallyHidden } from 'components/ui/visually-hidden';
import dayjs from 'dayjs';
import { useConversationsByType, useConversationsLoadingStatus } from 'hooks/useConversations';
import usePreferences from 'hooks/usePreferences';
import useScrollRestoration from 'hooks/useScrollRestoration';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ApiContentManager from 'services/api/ApiContentManager';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { QuestionnaireResponseDTO } from 'services/api/models/QuestionnaireDTO';
import { routes } from 'services/RouteService';
import { contentActions } from 'store/content';
import { selectSuggestedPosts } from 'store/content/selectors';
import { userActions } from 'store/users';
import { selectCurrentUser, selectHasAccessWithLicenseCode } from 'store/users/selectors';
import Drop from '../assets/drop.svg?react';
import homeTrackingExample from '../assets/home-tracking-example.png';

const COMPACT_SPECIALTIES = [
  { emoji: '🌸', key: 'chats.explainerV2.specialties.gynecologists' },
  { emoji: '🌿', key: 'chats.explainerV2.specialties.midwives' },
  { emoji: '🤲', key: 'chats.explainerV2.specialties.physiotherapists' },
  { emoji: '🥑', key: 'chats.explainerV2.specialties.nutritionists' },
  { emoji: '💬', key: 'chats.explainerV2.specialties.psychologists' },
  { emoji: '👶', key: 'chats.explainerV2.specialties.childcareNurses' },
  { emoji: '🦴', key: 'chats.explainerV2.specialties.osteopaths' },
  { emoji: '👩‍⚕️', key: 'chats.explainerV2.specialties.nurses' },
  { emoji: '🌱', key: 'chats.explainerV2.specialties.naturopaths' },
  { emoji: '🧘', key: 'chats.explainerV2.specialties.sophrologists' },
];

function Statistic({ title, value }: { title: string; value: number }) {
  return (
    <div>
      <div className="text-muted-foreground text-sm">{title}</div>
      <div className="text-2xl font-medium">{value}</div>
    </div>
  );
}

export default function Home() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const [shouldDisplaySurvey, setShouldDisplaySurvey] = useState(false);
  const areConversationsLoading = useConversationsLoadingStatus() === 'LOADING';
  const conversations = useConversationsByType('assigned');
  const unassignedConversations = useConversationsByType('unassigned');
  const myRequestsConversations = useConversationsByType('myRequest');
  const waitingForReassignmentConversations = useConversationsByType('waitingForReassignment');
  const hasAccessWithLicenseCode = useSelector(selectHasAccessWithLicenseCode);

  // Calculate total unread messages across all conversations
  const totalUnreadMessages = useMemo(() => {
    return conversations.reduce((total, conv) => total + (conv.number_of_unread_messages || 0), 0);
  }, [conversations]);

  const { data: questionnaireResponse } = useQuery<QuestionnaireResponseDTO | null>({
    queryKey: ['questionnaire', 'myResponse', currentUser?.id],
    queryFn: () =>
      ApiQuestionnaireManager.getMyResponse().catch(err => {
        if (err instanceof AxiosError && err.response?.status === 404) return null;
        throw err;
      }),
    enabled: Boolean(!currentUser?.is_caregiver),
    staleTime: 1000 * 60 * 5,
  });

  const { value: shouldAskForPeriodPreference } = usePreferences(Keys.TRACKING_SURVEY_SHOULD_ASK_FOR_PERIOD);
  const isPeriodTrackingEnabled = shouldAskForPeriodPreference !== false;

  const {
    getDisplayedDays,
    getStatusValueForDisplayedDays,
    cycleStats,
    hasTrackingBeenUsed,
    hasTrackingForToday,
    loading: loadingTracking,
    daysUntilNextPeriod,
    nextPeriodPrediction,
  } = useSymptomTrackingData();
  const displayedDays = getDisplayedDays(dayjs());
  const generalStateValues = getStatusValueForDisplayedDays('general_state', displayedDays);

  const { predictionLabel, predictionValue } = nextPeriodPrediction;

  const suggestedPosts = useSelector(selectSuggestedPosts);

  const { run: loadPosts, loading: isLoadingPosts } = useRequest(() => ApiContentManager.getPosts(), {
    manual: true,
    cacheKey: `content.suggestedPosts`,
    cacheTime: 1000 * 60 * 10, // 10 minutes
    onSuccess: data => {
      if (!suggestedPosts) {
        // storing suggested posts in the store to avoid fetching them again
        // as they are shuffled on the backend. This way, we can keep
        // the same order for a given session of a user.
        // This makes it feel more natural to the user, and also helps
        // when coming back to a list of articles from a specific article
        // as it keeps the same position in the list of suggested articles.
        dispatch(contentActions.setSuggestedPosts(data));
      }
    },
  });

  useEffect(() => {
    if (!suggestedPosts) {
      loadPosts();
    }
  }, [suggestedPosts, loadPosts]);

  const scrollableRef = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollableRef);

  const trackingGraphRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (trackingGraphRef.current) {
      trackingGraphRef.current.scrollLeft = trackingGraphRef.current.scrollWidth;
    }
  }, [trackingGraphRef, generalStateValues]);

  useEffect(() => {
    if (currentUser && currentUser.should_ask_for_nps && !currentUser.is_caregiver) {
      setShouldDisplaySurvey(true);
      ApiUsersManager.acknowledgeNPS();

      // update the current user locally to disable NPS
      dispatch(userActions.disableNPSSurvey());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const skeleton = (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-8 w-full" />
    </div>
  );

  const messagesCardForCaregivers = (
    <>
      <div className="mb-2 grid grid-cols-[7fr_7fr_10fr]">
        <Statistic
          title={t('home.messages.waitingForMe')}
          value={unassignedConversations.length + myRequestsConversations.length}
        />
        <Statistic title={t('home.messages.inProgress')} value={conversations.length} />
        <Statistic
          title={t('home.messages.waitingForReassignment')}
          value={waitingForReassignmentConversations.length}
        />
      </div>
      <Button size="xl" className="w-full" onClick={() => navigate(routes.messagingHome.route)}>
        {t('home.messages.goToMessages')}
      </Button>
    </>
  );

  const messagesCardWithoutMessages = (
    <>
      <div className="mt-2 mb-4 flex items-center gap-2.5">
        <img
          src={laurine}
          alt="Laurine"
          className="border-info-muted size-11 shrink-0 rounded-full border-2 object-cover"
        />
        <div>
          <p className="text-foreground m-0 text-sm leading-tight font-bold">{t('home.messages.referralName')}</p>
          <p className="text-muted-foreground m-0 text-xs leading-tight">{t('home.messages.referralTagline')}</p>
        </div>
      </div>
      <p className="text-foreground mt-0 mb-2 text-sm">{t('home.messages.tagline')}</p>
      {/* `min-w-0`: without it the strip below cannot scroll — `min-width: auto`
          on a flex item resolves to its content width, so the grid would widen
          its ancestors instead of overflowing them. */}
      <div className="relative mb-3 min-w-0">
        <div className="grid min-w-0 grid-flow-col grid-rows-[repeat(2,auto)] gap-x-1.5 gap-y-1 overflow-x-auto pr-6 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {COMPACT_SPECIALTIES.map(s => (
            <span
              key={s.key}
              // Primary ink, not a recessive tier: the metadata tier has no contrast margin on this fill for an 11.5px label.
              className="bg-info-muted text-foreground rounded-full px-2 py-0.5 text-2xs whitespace-nowrap"
            >
              {s.emoji} {t(s.key)}
            </span>
          ))}
        </div>
        <div className="to-card pointer-events-none absolute top-0 right-0 bottom-0.5 w-9 bg-linear-to-r from-transparent" />
      </div>
      <Button size="xl" className="w-full" onClick={() => navigate(routes.messagingHome.route)}>
        {t('home.messages.callToAction')}
      </Button>
    </>
  );

  const messagesCardWithMessages = (
    <>
      <ChatsList conversations={conversations} title={null} readOnlyConvs={false} maximumConversations={1} />
      <Button
        size="xl"
        className="w-full"
        data-testid="home-open-messaging-button"
        onClick={() => navigate(routes.messagingHome.route)}
      >
        {t('home.messages.goToMessages')}
        {totalUnreadMessages > 0 && (
          <Badge data-testid="unread-messages-badge" variant="success">
            {totalUnreadMessages}
          </Badge>
        )}
      </Button>
    </>
  );
  const trackingCardWithoutData = (
    <>
      <p>{t('home.tracking.description')}</p>
      <div className="mb-4 overflow-x-scroll overflow-y-hidden">
        <img src={homeTrackingExample} alt="" className="h-auto w-full" />
      </div>

      <Button size="xl" className="w-full" onClick={() => navigate(routes.symptomTracking.route)}>
        {t('home.tracking.fillSurvey')}
      </Button>
    </>
  );

  const periodPrediction = daysUntilNextPeriod && (
    <div>
      <p className="text-subtle-foreground m-0">{predictionLabel}</p>
      <p className="mt-0 text-right text-lg font-bold">
        <Drop fill="currentColor" className="text-chart-severity-very-bad mr-1 inline" height={11} width={11} />
        {predictionValue}
        {cycleStats.roundedStandardDeviation >= 1 && (
          <span className="text-subtle-foreground ml-2 font-normal">± {cycleStats.roundedStandardDeviation} jours</span>
        )}
      </p>
    </div>
  );

  const trackingCardWithData = (
    <>
      {isPeriodTrackingEnabled && daysUntilNextPeriod && (
        <div className="flex items-center gap-8">
          <></>
        </div>
      )}
      <div className="flex items-center justify-between">
        {hasTrackingBeenUsed && <p className="text-subtle-foreground m-0">{t('home.tracking.graphLabel')}</p>}
      </div>
      <div className="mb-4 overflow-x-scroll overflow-y-hidden" ref={trackingGraphRef}>
        <LinearCalendarChart dayLabels={displayedDays} dayValues={generalStateValues} range={[0, 4]} />
      </div>
      {hasTrackingForToday ? (
        <Button size="xl" className="w-full" onClick={() => navigate(routes.symptomTracking.route)}>
          {t('home.tracking.goToTracking')}
        </Button>
      ) : (
        <Button size="xl" className="w-full" onClick={() => navigate(routes.symptomTracking.route)}>
          {t('home.tracking.fillSurvey')}
        </Button>
      )}
    </>
  );

  const medicalProfileState = getMedicalProfileState(questionnaireResponse);
  const isUserEligibleForMedicalProfile = !currentUser?.is_caregiver && hasAccessWithLicenseCode;
  const showMedicalProfileAtTop =
    isUserEligibleForMedicalProfile &&
    questionnaireResponse !== undefined &&
    (medicalProfileState === 'not_started' ||
      medicalProfileState === 'in_progress' ||
      medicalProfileState === 'pending_review' ||
      medicalProfileState === 'in_review');
  const showMedicalProfileBelowTracking = isUserEligibleForMedicalProfile && medicalProfileState === 'reviewed';

  return (
    <Page>
      <PageTopBar
        title={
          <>
            {/* The wordmark *is* the heading, so the heading needs the words in
                it. An <svg> contributes no text, and `aria-label` on a bare
                <svg> is widely ignored, so without this the page heading is
                empty for screen readers and for the document outline. */}
            <span className="flex items-center gap-1">
              <Logo
                aria-hidden="true"
                focusable="false"
                fill="currentColor"
                className="text-foreground mt-2 mb-1 ml-2 block h-6 w-auto"
              />
              <Ribbon aria-hidden="true" width={24} height={24} className="" />
            </span>
            <VisuallyHidden>Gynger</VisuallyHidden>
          </>
        }
      />
      <PageContent className="[&_.rce-citem-avatar]:pl-0 [&_.rce-citem-body]:pr-0" ref={scrollableRef}>
        <h2 className="text-foreground mt-4 py-4 w-full text-center">
          {t('home.greeting', { firstName: currentUser?.first_name })}
        </h2>

        <RebrandingAnnouncementCard />

        {showMedicalProfileAtTop && <MedicalProfileCard response={questionnaireResponse} />}

        <ModuleCard title={t('home.messages.title')}>
          {areConversationsLoading
            ? skeleton
            : currentUser?.is_caregiver
              ? messagesCardForCaregivers
              : conversations.length > 0
                ? messagesCardWithMessages
                : messagesCardWithoutMessages}
        </ModuleCard>

        <OctobreRoseCard openedOnPage="home" />

        <ModuleCard title={t('home.tracking.title')} topRightContent={isPeriodTrackingEnabled && periodPrediction}>
          {loadingTracking ? skeleton : hasTrackingBeenUsed ? trackingCardWithData : trackingCardWithoutData}
        </ModuleCard>

        {showMedicalProfileBelowTracking && <MedicalProfileCard response={questionnaireResponse} />}

        {posthog.getFeatureFlag('prevention-program') === 'opt-in' && <PreventionJourneyModule />}

        <ModuleCard title={t('home.info.title')}>
          <div className="flex w-full">
            {isLoadingPosts ? (
              <div className="flex w-full flex-col gap-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <PostsList
                posts={suggestedPosts ?? []}
                limitPosts={4}
                posthogOnArticleOpenExtras={{ opened_on_page: 'home' }}
              />
            )}
          </div>
        </ModuleCard>
        {shouldDisplaySurvey && <span id="show-posthog-survey" />}

        {posthog.isFeatureEnabled('homepage-social-links-display') && (
          <>
            <div>
              <h4 className="mt-8 text-center text-muted-foreground">{t('home.social.title')}</h4>
              <p className="text-muted-foreground my-0 text-center text-sm font-normal">
                {t('home.social.description')}
              </p>
            </div>
            <div className="mb-4 flex flex-row items-center justify-center gap-6 pt-2">
              <a
                href={t('social.instagram')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center no-underline transition-opacity hover:opacity-70"
                aria-label="Instagram"
                onClick={() => {
                  posthog.capture('home.social_link_clicked', {
                    platform: 'instagram',
                  });
                }}
              >
                <span className="text-warning text-3xl leading-none">
                  <InstagramOutlined />
                </span>
              </a>
              <a
                href={t('social.tiktok')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center no-underline transition-opacity hover:opacity-70"
                aria-label="TikTok"
                onClick={() => {
                  posthog.capture('home.social_link_clicked', {
                    platform: 'tiktok',
                  });
                }}
              >
                <span className="text-warning text-3xl leading-none">
                  <TikTokOutlined />
                </span>
              </a>
            </div>
          </>
        )}
        <PartnerSection />
      </PageContent>
    </Page>
  );
}
